/**
 * Edit a client — FR-B1's other half, and ACCEPTANCE §3.1. On the kit's
 * fields and the Branches dialog's layout since Wave 15B (C14 §10.6 item 7;
 * T-6.14): four groups — *Name*, *Contact*, *Identifiers* and *Filing* —
 * each a heading over label-beside-field rows that stack on a phone. What
 * it asks and sends is unchanged.
 *
 * **There was no way to do this at all.** `PATCH /api/v1/clients/{id}` worked
 * and `clients.update` existed in the web client; neither had a caller, so from
 * the day the product shipped a client's email, mobile, address, registrations
 * and branch could not be recorded through it, and a typo in a name or a PAN
 * could not be corrected. FR-B1 names *contact details*, *registrations held*
 * and *a branch* and the add dialog collects none of the three.
 *
 * The cost was not the typo. **`notify` needs `client.email`** and skips with
 * `no_address` when there is none — so *"a client-visible stage notifies the
 * client"*, spec §5 item 3's most ordinary sentence, was unreachable for every
 * client a firm had ever added. The acceptance run only saw a notification
 * succeed because it set the address through the API by hand.
 *
 * Three decisions, none of them layout:
 *
 * - **What a client *is* is not editable here.** No kind, no constitution, no
 *   company type — that is a conversion (FR-B8), which is audited, published
 *   and has its own dialog beside this one. An edit form offering the three
 *   would be a second and unaudited way to do the same thing.
 * - **The PAN is optional and stays optional** (ADR-0016). No `required`, and
 *   `editProblems` refuses only a half-typed one. Clearing the box clears the
 *   PAN, which the service accepts and which an advocate's client needs.
 * - **Only what changed is sent, an emptied field as `null`** (`editBody`
 *   against the form as it opened). Until C27 every field shown was sent, and
 *   two people editing the same client in turn had the second silently put
 *   back what the first had changed (UAT report #20). The body now also carries
 *   `expected_updated_at`, and a save made against an older copy is refused
 *   `409 CONFLICT` (reason `stale`): the dialog says so and offers *Reload*,
 *   which reads the client again and refills the form.
 *
 * C27's rules are said under their fields before anything is sent — the PAN's
 * fourth character against what the client is (ADR-0057), a name with a
 * letter, a GSTIN against the state — each asked only of a field this edit
 * changes, so an old record is not refused over a value nobody touched. A
 * `VALIDATION_ERROR` naming a field lands under that field. The one required
 * field carries the red star, and nothing marks the optional ones (#19).
 */

import { Alert, Button, Dialog, Input, Select, Textarea } from '@fiveox/ui'
import { Plus, X } from 'lucide-react'
import { type ReactNode, useEffect, useState } from 'react'

import { DialogActions, FORM_GRID, FormRow } from '../../components/page'
import { ApiError } from '../../lib/api'
import {
  type ClientEdit,
  type ClientRecord,
  REGISTRATION_LABELS,
  STALE,
  type Vocabulary,
  clients,
  conflictReason,
  editBody,
  editFrom,
  editMessages,
  refusedField,
} from '../../lib/api/clients'
import { type Branch, firm } from '../../lib/api/firm'

/** A group's heading and its muted line, spanning the grid. */
function Group({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="border-border mt-2 mb-2 border-t pt-4 first:mt-0 first:border-t-0 first:pt-0 sm:col-span-2 sm:mb-0">
      <h3 className="text-foreground text-sm font-semibold">{title}</h3>
      {children && <p className="text-muted-foreground mt-0.5 text-xs">{children}</p>}
    </div>
  )
}

/** What the dialog says when the client changed under it (C27 item 4). */
export const STALE_COPY = 'Someone else changed this client after you opened it.'

export function EditClientDialog({
  client,
  onClose,
  onSaved,
  onReloaded,
}: {
  client: ClientRecord
  onClose: () => void
  onSaved: () => void
  /** Called with the fresh record after *Reload*, so the page behind the
   *  dialog stops showing the copy that went stale. */
  onReloaded?: (client: ClientRecord) => void
}) {
  /** The copy this edit is made against: what `editBody` diffs with and whose
   *  `updated_at` the save names. *Reload* replaces it. */
  const [opened, setOpened] = useState<ClientRecord>(client)
  const [edit, setEdit] = useState<ClientEdit>(() => editFrom(client))
  const [stale, setStale] = useState(false)
  const [reloading, setReloading] = useState(false)
  const [refused, setRefused] = useState<{ field: string; message: string } | null>(null)
  const [vocabulary, setVocabulary] = useState<Vocabulary | null>(null)
  const [branches, setBranches] = useState<Branch[]>([])
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // Both served rather than hard-coded, and **neither is fatal**: a firm with
  // no branches, or one whose vocabulary read fails, still gets a form that can
  // save an email address. Refusing to render over a failed side read is how a
  // screen that exists becomes a screen that might as well not.
  useEffect(() => {
    clients
      .vocabulary()
      .then((body) => setVocabulary(body.data))
      .catch(() => setVocabulary(null))
    firm
      .branches('active')
      .then((body) => setBranches(body.data))
      .catch(() => setBranches([]))
  }, [])

  const registrationKinds = vocabulary?.registration_kinds ?? Object.keys(REGISTRATION_LABELS)
  const messages = editMessages(edit, opened)
  const problems = Object.keys(messages)
  const changes = editBody(edit, editFrom(opened))
  const unchanged = Object.keys(changes).length === 0
  const fieldError = (field: keyof ClientEdit): string | null =>
    refused?.field === field ? refused.message : (messages[field] ?? null)
  const set = (next: Partial<ClientEdit>) => {
    if (refused && refused.field in next) setRefused(null)
    setEdit((current) => ({ ...current, ...next }))
  }

  const setRegistration = (index: number, changes: Partial<(typeof edit.registrations)[number]>) =>
    setEdit((current) => ({
      ...current,
      registrations: current.registrations.map((row, at) =>
        at === index ? { ...row, ...changes } : row,
      ),
    }))

  const addRegistration = () =>
    setEdit((current) => ({
      ...current,
      registrations: [
        ...current.registrations,
        { kind: registrationKinds[0] ?? 'other', value: '', label: null },
      ],
    }))

  const removeRegistration = (index: number) =>
    setEdit((current) => ({
      ...current,
      registrations: current.registrations.filter((_row, at) => at !== index),
    }))

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setError(null)
    setRefused(null)
    try {
      await clients.update(opened.id, {
        ...changes,
        ...(opened.updated_at ? { expected_updated_at: opened.updated_at } : {}),
      })
      onSaved()
    } catch (err) {
      const onField = refusedField(err)
      if (conflictReason(err) === STALE) setStale(true)
      else if (onField && onField.field in edit) setRefused(onField)
      // `CONFLICT` here is otherwise *this firm's own* record holding that PAN
      // — safe to name, and the service's message does. Anything else gets a
      // sentence that does not pretend to know.
      else setError(err instanceof ApiError ? err.message : 'The changes could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  /** Read the client again and start the form over from it. What this person
   *  had typed is dropped on purpose: merging it over somebody else's change is
   *  the silent overwrite the refusal exists to prevent. */
  const reload = async () => {
    setReloading(true)
    setError(null)
    try {
      const fresh = (await clients.get(opened.id)).data
      setOpened(fresh)
      setEdit(editFrom(fresh))
      setRefused(null)
      setStale(false)
      onReloaded?.(fresh)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'This client could not be read again.')
    } finally {
      setReloading(false)
    }
  }

  return (
    <Dialog open onClose={onClose} title="Edit client" error={error} width="lg">
      <form onSubmit={submit}>
        {stale && (
          <Alert tone="warning" className="mb-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>{STALE_COPY} Reload to see their changes, then make yours again.</span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void reload()}
                loading={reloading}
                loadingText="Reloading…"
              >
                Reload
              </Button>
            </div>
          </Alert>
        )}
        <div className={FORM_GRID}>
          <Group title="Name">
            To change whether this is an individual or a business, use Convert — that is recorded
            on the client’s history.
          </Group>
          <FormRow
            label={opened.kind === 'individual' ? 'Full name' : 'Legal name'}
            htmlFor="edit-client-name"
            required
          >
            <Input
              id="edit-client-name"
              value={edit.legal_name}
              onChange={(e) => set({ legal_name: e.target.value })}
              required
              aria-required="true"
              maxLength={200}
              error={fieldError('legal_name')}
            />
          </FormRow>
          <FormRow label="Display name" htmlFor="edit-client-display">
            <Input
              id="edit-client-display"
              value={edit.display_name}
              onChange={(e) => set({ display_name: e.target.value })}
              maxLength={200}
              error={fieldError('display_name')}
              hint="What the firm calls them day to day."
            />
          </FormRow>

          <Group title="Contact">
            An email address is what the client’s notifications are sent to. Without one, a
            client-visible stage changes quietly and the client hears nothing.
          </Group>
          <FormRow label="Email" htmlFor="edit-client-email">
            <Input
              id="edit-client-email"
              type="email"
              value={edit.email}
              onChange={(e) => set({ email: e.target.value })}
              maxLength={255}
              placeholder="books@acme.example"
            />
          </FormRow>
          <FormRow label="Mobile" htmlFor="edit-client-mobile">
            <Input
              id="edit-client-mobile"
              value={edit.mobile}
              onChange={(e) => set({ mobile: e.target.value })}
              maxLength={20}
              placeholder="+91 98200 00000"
            />
          </FormRow>
          <FormRow label="Address" htmlFor="edit-client-address">
            <Textarea
              id="edit-client-address"
              value={edit.address}
              onChange={(e) => set({ address: e.target.value })}
              maxLength={400}
              rows={2}
            />
          </FormRow>
          <FormRow label="City" htmlFor="edit-client-city">
            <Input
              id="edit-client-city"
              value={edit.city}
              onChange={(e) => set({ city: e.target.value })}
              maxLength={100}
            />
          </FormRow>
          <FormRow label="State" htmlFor="edit-client-state">
            <Input
              id="edit-client-state"
              value={edit.state}
              onChange={(e) => set({ state: e.target.value })}
              maxLength={100}
              error={fieldError('state')}
            />
          </FormRow>
          <FormRow label="PIN code" htmlFor="edit-client-pincode">
            <Input
              id="edit-client-pincode"
              value={edit.pincode}
              onChange={(e) => set({ pincode: e.target.value })}
              maxLength={10}
              inputMode="numeric"
            />
          </FormRow>

          <Group title="Identifiers" />
          <FormRow label="PAN" htmlFor="edit-client-pan">
            <Input
              id="edit-client-pan"
              value={edit.pan}
              onChange={(e) => set({ pan: e.target.value })}
              maxLength={10}
              placeholder="AAAPA1234A"
              className="font-mono uppercase"
              error={fieldError('pan')}
              hint="Leave this empty if the client has no PAN."
            />
          </FormRow>
          <FormRow label="GSTIN" htmlFor="edit-client-gstin">
            <Input
              id="edit-client-gstin"
              value={edit.gstin}
              onChange={(e) => set({ gstin: e.target.value })}
              maxLength={15}
              placeholder="27AAAPA1234A1Z5"
              className="font-mono uppercase"
              error={fieldError('gstin')}
            />
          </FormRow>

          <FormRow label="Registrations held">
            <fieldset className="min-w-0">
              <legend className="sr-only">Registrations held</legend>
              {edit.registrations.length === 0 && (
                <p className="text-muted-foreground mb-2 text-xs">
                  TAN, IEC, CIN, a bar enrolment — whatever this client holds.
                </p>
              )}
              <ul className="space-y-2">
                {edit.registrations.map((registration, index) => (
                  // The index **is** the identity here: two rows may legitimately
                  // share a kind and an empty value while they are being typed, so
                  // there is no field that is unique yet.
                  <li key={index} className="flex items-start gap-2">
                    <div className="w-32 shrink-0 sm:w-40">
                      <Select
                        aria-label="Registration kind"
                        value={registration.kind}
                        onChange={(e) => setRegistration(index, { kind: e.target.value })}
                      >
                        {registrationKinds.map((kind) => (
                          <option key={kind} value={kind}>
                            {REGISTRATION_LABELS[kind] ?? kind}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <div className="min-w-0 flex-1">
                      <Input
                        value={registration.value}
                        onChange={(e) => setRegistration(index, { value: e.target.value })}
                        placeholder="Number"
                        aria-label="Registration number"
                        className="font-mono"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => removeRegistration(index)}
                      aria-label="Remove this registration"
                    >
                      <X size={16} aria-hidden />
                    </Button>
                  </li>
                ))}
              </ul>
              <Button type="button" variant="outline" size="sm" className="mt-2" onClick={addRegistration}>
                <Plus size={14} aria-hidden /> Add a registration
              </Button>
              {fieldError('registrations') && (
                <p className="text-destructive mt-1.5 text-xs">{fieldError('registrations')}</p>
              )}
            </fieldset>
          </FormRow>

          <Group title="Filing" />
          <FormRow label="Branch" htmlFor="edit-client-branch">
            <Select
              id="edit-client-branch"
              value={edit.branch_id}
              onChange={(e) => set({ branch_id: e.target.value })}
              disabled={branches.length === 0}
              hint={
                branches.length === 0
                  ? 'This firm has one office, so there is nothing to choose.'
                  : 'New cases for this client start at this branch.'
              }
            >
              <option value="">Not set</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                  {branch.is_default ? ' (default)' : ''}
                </option>
              ))}
            </Select>
          </FormRow>
          <FormRow label="Tags" htmlFor="edit-client-tags">
            <Input
              id="edit-client-tags"
              value={edit.tags}
              onChange={(e) => set({ tags: e.target.value })}
              placeholder="retainer, gst"
              hint="Comma separated."
            />
          </FormRow>
        </div>

        <DialogActions>
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          {/* Nothing changed is nothing to send — and with the body now only
              the changes, an empty PATCH would be a save that did nothing. */}
          <Button
            type="submit"
            disabled={problems.length > 0 || unchanged || stale}
            loading={saving}
            loadingText="Saving…"
          >
            Save changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
